import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { LOCAL_STORAGE_NAMES, UserType } from '../constants/constants';
import { useUser } from './UserContext';
import { FeatureFlags, FeatureSelectionTab, StartModule } from '../interface/featureSelection';
import {
  DEFAULT_START_MODULE,
  readStoredStartModule,
  resolveStartModule,
  storeStartModule,
} from '../utils/startModule';

const RESTRICTED_USER_FEATURES = [
  FeatureSelectionTab.invoicing,
  FeatureSelectionTab.conflictSearch,
];

const ALWAYS_ENABLED_FEATURES: FeatureFlags = {
  [FeatureSelectionTab.dashboard]: true,
  [FeatureSelectionTab.timer]: true,
  [FeatureSelectionTab.transactions]: true,
  [FeatureSelectionTab.profile]: true,
};

/** Returns a copy, so the shared module-level defaults are never written through. */
const withRestrictionsApplied = (features: FeatureFlags, isRestricted: boolean): FeatureFlags => {
  const resolved = { ...features };
  if (!isRestricted) return resolved;
  RESTRICTED_USER_FEATURES.forEach((feature) => {
    resolved[feature] = false;
  });
  return resolved;
};

const readStoredFeaturesForUser = (userId: string): FeatureFlags | undefined => {
  const stored = localStorage.getItem(LOCAL_STORAGE_NAMES.featureSelections);
  if (!stored) return undefined;
  return JSON.parse(stored)[userId];
};

/** Always-on features override a stored opt-out, so a user who switched one off before it became always on gets it back. */
const readFeaturesForUser = (userId: string | undefined, isRestricted: boolean): FeatureFlags => {
  if (!userId) return {};
  const storedFeatures = readStoredFeaturesForUser(userId);
  return withRestrictionsApplied({ ...storedFeatures, ...ALWAYS_ENABLED_FEATURES }, isRestricted);
};

/** Features are saved from workspace setup onwards, so a user who has some has already been through it. */
export const hasCompletedWorkspaceSetup = (userId: string): boolean =>
  readStoredFeaturesForUser(userId) !== undefined;

export const readStartModuleForUser = (userId: string, isRestricted: boolean): StartModule =>
  resolveStartModule(readFeaturesForUser(userId, isRestricted), readStoredStartModule(userId));

const storeFeaturesForUser = (userId: string, features: FeatureFlags) => {
  const stored = localStorage.getItem(LOCAL_STORAGE_NAMES.featureSelections);
  const allUsersFeatures = stored ? JSON.parse(stored) : {};
  allUsersFeatures[userId] = features;
  localStorage.setItem(LOCAL_STORAGE_NAMES.featureSelections, JSON.stringify(allUsersFeatures));
};

/** Returns the start module to keep once the features are saved: Dashboard if the stored one is now switched off. */
const resetStartModuleIfSwitchedOff = (userId: string, features: FeatureFlags): StartModule => {
  const storedStartModule = readStoredStartModule(userId);
  const startModule = resolveStartModule(features, storedStartModule);
  if (startModule !== storedStartModule) storeStartModule(userId, startModule);
  return startModule;
};

const FeatureContext = createContext<{
  features: FeatureFlags;
  updateFeatures: (newFeatures: FeatureFlags) => void;
  startModule: StartModule;
  updateStartModule: (startModule: StartModule) => void;
}>({
  features: {},
  updateFeatures: () => {},
  startModule: DEFAULT_START_MODULE,
  updateStartModule: () => {},
});

export const FeatureProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useUser();
  const isRestricted = user?.userType === UserType.RESTRICTED;
  const userId = user?.id;
  // Read up front, so the route the add-in opens on is resolved from the saved settings on the first render.
  const [features, setFeatures] = useState<FeatureFlags>(() =>
    readFeaturesForUser(userId, isRestricted),
  );
  const [storedStartModule, setStoredStartModule] = useState<StartModule>(() =>
    readStoredStartModule(userId),
  );
  const [loadedUser, setLoadedUser] = useState({ userId, isRestricted });

  // Re-read while rendering, not in an effect, so the first screen after login never shows the previous user's settings.
  if (userId && (userId !== loadedUser.userId || isRestricted !== loadedUser.isRestricted)) {
    setLoadedUser({ userId, isRestricted });
    setFeatures(readFeaturesForUser(userId, isRestricted));
    setStoredStartModule(readStoredStartModule(userId));
  }

  const updateFeatures = useCallback(
    (newFeatures: FeatureFlags) => {
      if (!userId) return;
      const updatedFeatures = withRestrictionsApplied(
        { ...newFeatures, ...ALWAYS_ENABLED_FEATURES },
        isRestricted,
      );
      setFeatures(updatedFeatures);
      storeFeaturesForUser(userId, updatedFeatures);
      setStoredStartModule(resetStartModuleIfSwitchedOff(userId, updatedFeatures));
    },
    [userId, isRestricted],
  );

  const updateStartModule = useCallback(
    (startModule: StartModule) => {
      if (!userId) return;
      storeStartModule(userId, startModule);
      setStoredStartModule(startModule);
    },
    [userId],
  );

  const startModule = resolveStartModule(features, storedStartModule);

  const featureContextValue = useMemo(
    () => ({ features, updateFeatures, startModule, updateStartModule }),
    [features, updateFeatures, startModule, updateStartModule],
  );
  return <FeatureContext.Provider value={featureContextValue}>{children}</FeatureContext.Provider>;
};

export const useFeatureContext = () => useContext(FeatureContext);
